import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormFeedback, FormGroup, Label } from 'reactstrap';

import { DropTarget, DRAG_TYPES } from '@techchange/dnd';
import { IconButton } from '../../../../../_shared';
import { messages } from '../../../../../_shared/i18n/messages';

import { FORM_TYPES } from '../../../../constants';
import { useFormEditorContext } from '../../contexts/FormEditorContext';
import {
	FORM_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from '../../hooks/useFormEditorData';
import {
	formSectionDataShape,
	sectionInputValidationShape,
} from '../../utils/propTypes';
import FormNavigationOptionsDropdown, { FORM_ITEM_TYPE_SECTION } from
	'../FormEditorField/components/FormNavigationOptionsDropdown';
import FormEditorQuestionDropdownMenu from
	'../FormEditorQuestionDropdownMenu';

import AdvancedSettingsModal from './AdvancedSettingsModal';

import './FormEditorSection.scss';

const propTypes = {

	/**
	 * The field-level data for the form section.
	 */
	section: formSectionDataShape.isRequired,

	/**
	 * The input validation for the form section.
	 */
	sectionValidation: sectionInputValidationShape,

	/**
	 * Flag indicating if the "Advanced" option should be visible
	 * in the navigation settings dropdown.
	 */
	showAdvancedOption: PropTypes.bool,

	/**
	 * The index of the section with respect to the ordered
	 * list of sections in the form.
	 */
	sectionIndex: PropTypes.number.isRequired,

	/**
	 * Function to choose a drag source, injected by react-dnd.
	 */
	connectDragSource: PropTypes.func,

	/**
	 * The component children, if any.
	 */
	children: PropTypes.node,

};

/**
 * Shared component to render a section in the form editor.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const FormEditorSection = (props) => {

	const {
		section,
		sectionValidation,
		showAdvancedOption,
		sectionIndex,
		children,
		connectDragSource
	} = props;

	const intl = useIntl();

	const [showAdvancedSettingsModal, setShowAdvancedSettingsModal] = useState(false);

	const {
		formData,
		defaultLanguage,
		selectedLanguage,
		isEditingDefaultLanguage,
		actions,
	} = useFormEditorContext();

	// Get the form section content data for the selected language
	const activeTranslation =
		section[SECTION_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === selectedLanguage
		);

	// Get the form section content data for the default language
	const defaultTranslation =
		section[SECTION_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === defaultLanguage
		);

	// Get the validation related to the form section content data and selected language
	const validationState =
		sectionValidation?.[SECTION_EDITOR_FIELDS.CONTENTS]?.data?.[selectedLanguage];

	// Build the placeholder text for the section title value

	let titlePlaceholderText = sectionIndex === 0
		? intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_LABEL_TEXT_FORM_TITLE)
		: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_LEGEND);

	if (!isEditingDefaultLanguage && defaultTranslation?.title) {
		titlePlaceholderText = defaultTranslation?.title;
	}

	// Build the placeholder text for the section description value

	let descriptionPlaceholderText = sectionIndex === 0
		? intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_LABEL_TEXT_FORM_DESCRIPTION)
		: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_DESCRIPTION);

	if (!isEditingDefaultLanguage && defaultTranslation?.description) {
		descriptionPlaceholderText = defaultTranslation?.description;
	}

	const isSectionEmpty = section[SECTION_EDITOR_FIELDS.FIELDS]?.length === 0;
	const showNavOptions =
		formData[FORM_EDITOR_FIELDS.SECTIONS].length > 1 &&
		formData[FORM_EDITOR_FIELDS.TYPE] === FORM_TYPES.APPLICATION;

	const isTitleInvalid = validationState?.[SECTION_EDITOR_FIELDS.TITLE]?.invalid;

	return (
		<Fragment>
			<div className="form-editor-section-header">
				<FormattedMessage
					{...messages.TC_COMPONENTS_FORM_EDITOR_SECTION_HEADER}
					values={{
						sectionNumber: sectionIndex + 1,
						numSections: formData[FORM_EDITOR_FIELDS.SECTIONS].length,
					}} />
			</div>
			<FormGroup className="tc-form-editor-section">
				{isEditingDefaultLanguage &&
					<div className="d-flex justify-content-center">
						<div className="tc-form-editor-section-action-dnd">
							{connectDragSource && connectDragSource(
								<div
									className="tc-form-editor-field-action drag-source-handle"
									title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_MOVE_SECTION)}>
									<i className="fas fa-grip" />
								</div>
							)}
						</div>
					</div>
				}
				<Label
					for={`${section.id}-title`}
					hidden>
					{sectionIndex === 0
						? intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_LABEL_TEXT_FORM_TITLE)
						: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_LEGEND)
					}
				</Label>
				<input
					className={`
						tc-material-input tc-material-input-h5
						${isTitleInvalid ? 'tc-material-input-invalid' : ''}
					`}
					aria-describedby={
						isTitleInvalid ? `${section.id}-title-feedback` : undefined
					}
					type="text"
					name={`${section.id}-title`}
					placeholder={titlePlaceholderText}
					value={activeTranslation?.[SECTION_EDITOR_FIELDS.TITLE] || ''}
					onChange={(event) => actions.sections.update(
						section.id,
						SECTION_EDITOR_FIELDS.TITLE,
						event.target.value,
					)} />
				{isTitleInvalid &&
					<FormFeedback
						id={`${section.id}-title-feedback`}
						aria-live="assertive">
						{validationState?.[SECTION_EDITOR_FIELDS.TITLE]?.error}
					</FormFeedback>
				}
				<Label
					for={`${section.id}-description`}
					hidden>
					{sectionIndex === 0
						? intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_LABEL_TEXT_FORM_DESCRIPTION)
						: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_DESCRIPTION)}
				</Label>
				<input
					className="tc-material-input"
					type="text"
					name={`${section.id}-description`}
					placeholder={descriptionPlaceholderText}
					value={activeTranslation?.[SECTION_EDITOR_FIELDS.DESCRIPTION] || ''}
					onChange={(event) => actions.sections.update(
						section.id,
						SECTION_EDITOR_FIELDS.DESCRIPTION,
						event.target.value,
					)} />
				{isSectionEmpty &&
					<DropTarget
						direction="vertical"
						type={DRAG_TYPES.FORM_FIELD}
						item={{ form_section_id: section.id }}
						prevItem={null}
						nextItem={null}>
						<div className="m-0 py-3" />
					</DropTarget>
				}
				<div className="form-section-fields">
					{children}
				</div>
				{isEditingDefaultLanguage &&
					<div
						className={`
							tc-form-editor-question-dropdown-container
							${isSectionEmpty ? 'mt-0' : ''}
						`}>
						<FormEditorQuestionDropdownMenu
							type={formData[FORM_EDITOR_FIELDS.TYPE]}
							section
							onAddQuestion={(type) => actions.fields.add(type, section.id)} />
					</div>
				}
				{isEditingDefaultLanguage && (showNavOptions || sectionIndex > 0) &&
					<div className="tc-form-editor-section-actionsbar">
						{showNavOptions &&
							<Fragment>
								<div className="tc-form-editor-section-prompt">
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_NAVIGATION_OPTIONS_SECTION_PROMPT}
										values={{ number: sectionIndex + 1 }} />
								</div>
								<FormNavigationOptionsDropdown
									formItem={section}
									formItemType={FORM_ITEM_TYPE_SECTION}
									sections={formData[FORM_EDITOR_FIELDS.SECTIONS]}
									showAdvancedOption={showAdvancedOption}
									onChange={actions.sections.update}
									onToggleAdvancedModal={() => setShowAdvancedSettingsModal((prev) => !prev)} />
								<AdvancedSettingsModal
									currentFormSection={section}
									sections={formData[FORM_EDITOR_FIELDS.SECTIONS]}
									isOpen={showAdvancedSettingsModal}
									onChange={actions.sections.updateAdvancedSetting}
									onSave={(objectType) => {

										actions.sections.update(
											section.id,
											SECTION_EDITOR_FIELDS.CONTINUATION_TYPE,
											objectType,
										);

										actions.sections.update(
											section.id,
											SECTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID,
											null,
										);

										setShowAdvancedSettingsModal(false);

									}}
									onCancel={() => setShowAdvancedSettingsModal(false)} />
							</Fragment>
						}
						{sectionIndex > 0 &&
							<IconButton
								className="tc-form-editor-field-action ms-auto"
								title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_REMOVE_SECTION)}
								aria-label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_ARIA_LABEL_REMOVE_SECTION, {
									section_number: sectionIndex + 1,
								})}
								icon="fas fa-trash fa-lg"
								size="35px"
								onClick={() => actions.sections.delete(section.id)} />
						}
					</div>
				}
			</FormGroup>
		</Fragment>
	);

};

FormEditorSection.propTypes = propTypes;

export default FormEditorSection;
